import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { VerificationCodeInput } from './VerificationCodeInput';
import { sendPasswordResetCode, verifyPasswordResetCode, resetPassword } from '@/lib/auth';
import { showNotification } from '@/lib/generalNotifications';
import { Mail, Lock, AlertCircle, KeyRound, Check, ArrowRight, ChevronLeft } from 'lucide-react';

interface ForgotPasswordDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ForgotPasswordDialog({ isOpen, onClose }: ForgotPasswordDialogProps) {
  const [step, setStep] = useState<'email' | 'verify' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isGoogleUser, setIsGoogleUser] = useState(false);
  
  // Reset all states when dialog is closed
  const handleClose = () => {
    setStep('email');
    setEmail('');
    setVerificationCode('');
    setPassword('');
    setConfirmPassword('');
    setLoading(false);
    setError('');
    setIsGoogleUser(false);
    onClose();
  };
  
  // Validate email
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  
  // Validate password (at least 8 characters, 1 uppercase, 1 lowercase, 1 number)
  const isValidPassword = (password: string) => {
    return password.length >= 8 && 
           /[A-Z]/.test(password) && 
           /[a-z]/.test(password) && 
           /[0-9]/.test(password);
  };
  
  // Handle email submit to request verification code
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await sendPasswordResetCode(email);
      
      if (result.success) {
        setStep('verify');
        setEmail(email);
        setError('');
        
        // Show success message
        showNotification({
          title: 'Verification Code Sent',
          message: result.message,
          type: 'success'
        });
      } else {
        setError(result.message);
        
        // Check for Google user specifically if that error is returned
        if (result.error === 'GOOGLE_USER') {
          setIsGoogleUser(true);
        }
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle verification code submission
  const handleVerifyCode = async (code: string) => {
    setVerificationCode(code);
    setLoading(true);
    setError('');
    
    try {
      const result = await verifyPasswordResetCode(email, code);
      
      if (result.success) {
        setStep('reset');
        setError('');
        
        // Show success message
        showNotification({
          title: 'Code Verified',
          message: 'Verification successful. Please set your new password.',
          type: 'success'
        });
      } else {
        setError(result.message);
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle password reset
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidPassword(password)) {
      setError('Password must be at least 8 characters with at least one uppercase letter, one lowercase letter, and one number');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const result = await resetPassword(email, password, verificationCode);
      
      if (result.success) {
        // Show success message
        showNotification({
          title: 'Password Reset Successful',
          message: 'Your password has been reset. You can now log in with your new password.',
          type: 'success'
        });
        
        // Close the dialog after successful reset
        handleClose();
      } else {
        setError(result.message);
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (step === 'verify') {
      setStep('email');
    } else if (step === 'reset') {
      setStep('verify');
    }
    setError('');
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-md rounded-xl p-0 overflow-hidden">
        {/* Decorative header background */}
        <div className="absolute inset-0 h-24 bg-gradient-to-r from-primary/90 to-primary/70 -z-10" />
        
        <div className="pt-6">
          <DialogHeader className="px-6">
            <DialogTitle className="text-2xl font-bold">
              {step === 'email' && 'Reset Your Password'}
              {step === 'verify' && 'Verification Code'}
              {step === 'reset' && 'Create New Password'}
            </DialogTitle>
            <DialogDescription className="text-base opacity-80">
              {step === 'email' && 'Enter your email to receive a verification code'}
              {step === 'verify' && 'Enter the 6-digit code sent to your email'}
              {step === 'reset' && 'Choose a strong password for your account'}
            </DialogDescription>
          </DialogHeader>
        </div>
        
        {/* Back button for multi-step process */}
        {(step === 'verify' || step === 'reset') && (
          <button 
            onClick={goBack}
            className="absolute top-6 left-6 p-2 rounded-full hover:bg-black/10 transition-colors duration-200"
            disabled={loading}
            aria-label="Go back"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        )}
        
        {isGoogleUser ? (
          <div className="p-6 text-center">
            <div className="bg-gray-50 rounded-xl p-6 max-w-sm mx-auto">
              <div className="flex justify-center mb-4">
                <svg className="h-16 w-16" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-semibold mb-3">Google Sign-In Account</h3>
              <p className="text-gray-600 mb-6">
                This email is registered with Google Sign-In. Please use the "Sign in with Google" button on 
                the login page to access your account.
              </p>
              <Button 
                onClick={handleClose} 
                className="w-full py-6"
                size="lg"
              >
                Return to Login
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-6">
            {step === 'email' && (
              <form onSubmit={handleEmailSubmit} className="space-y-5">
                <div className="space-y-3">
                  <Label htmlFor="email" className="text-sm font-medium">Email address</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <Input 
                      id="email"
                      placeholder="you@example.com"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 py-6 text-base"
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                </div>
                
                {error && (
                  <div className="flex items-center px-4 py-3 rounded-lg bg-destructive/10 text-destructive gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                
                <div className="pt-2">
                  <Button 
                    type="submit" 
                    className="w-full py-6 text-base"
                    size="lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="h-5 w-5 border-2 border-white border-opacity-50 border-t-white rounded-full animate-spin" />
                        Sending code...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 justify-center">
                        Send Code
                        <ArrowRight className="h-5 w-5" />
                      </span>
                    )}
                  </Button>
                </div>
                
                <div className="flex justify-center">
                  <Button 
                    variant="ghost" 
                    onClick={handleClose}
                    className="text-sm"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
            
            {step === 'verify' && (
              <div className="space-y-6">
                <div className="bg-primary/5 rounded-xl p-4 text-center mb-2">
                  <h3 className="text-sm font-medium text-primary mb-1">
                    Verification code sent
                  </h3>
                  <p className="text-xs text-gray-500">
                    We've sent a 6-digit code to <span className="font-medium">{email}</span>
                  </p>
                </div>
                
                <div className="py-2">
                  <VerificationCodeInput 
                    onComplete={handleVerifyCode}
                    disabled={loading}
                  />
                </div>
                
                {error && (
                  <div className="flex items-center px-4 py-3 rounded-lg bg-destructive/10 text-destructive gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                
                {loading && (
                  <div className="flex justify-center items-center gap-2 py-4 text-primary">
                    <div className="h-5 w-5 border-2 border-primary border-opacity-50 border-t-primary rounded-full animate-spin" />
                    <span className="text-sm">Verifying code...</span>
                  </div>
                )}
                
                <div className="text-center text-xs text-gray-500 pt-2">
                  Didn't receive a code?{' '}
                  <Button 
                    variant="link" 
                    className="text-xs p-0 h-auto"
                    onClick={() => setStep('email')}
                    disabled={loading}
                  >
                    Try again
                  </Button>
                </div>
              </div>
            )}
            
            {step === 'reset' && (
              <form onSubmit={handleResetPassword} className="space-y-5">
                <div className="space-y-3">
                  <Label htmlFor="new-password" className="text-sm font-medium">New Password</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <Input 
                      id="new-password"
                      placeholder="New password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 py-6 text-base"
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                  <p className="text-xs text-gray-500 flex flex-wrap gap-1 items-center">
                    <span className={password.length >= 8 ? "text-green-600 font-medium" : ""}>8+ characters</span>
                    <span className="text-gray-300">•</span>
                    <span className={/[A-Z]/.test(password) ? "text-green-600 font-medium" : ""}>Uppercase</span>
                    <span className="text-gray-300">•</span>
                    <span className={/[a-z]/.test(password) ? "text-green-600 font-medium" : ""}>Lowercase</span>
                    <span className="text-gray-300">•</span>
                    <span className={/[0-9]/.test(password) ? "text-green-600 font-medium" : ""}>Number</span>
                  </p>
                </div>
                
                <div className="space-y-3">
                  <Label htmlFor="confirm-password" className="text-sm font-medium">Confirm Password</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <KeyRound className="h-5 w-5 text-gray-400" />
                    </div>
                    <Input 
                      id="confirm-password"
                      placeholder="Confirm password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10 py-6 text-base"
                      disabled={loading}
                    />
                  </div>
                </div>
                
                {error && (
                  <div className="flex items-center px-4 py-3 rounded-lg bg-destructive/10 text-destructive gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}
                
                <div className="pt-2">
                  <Button 
                    type="submit" 
                    className="w-full py-6 text-base"
                    size="lg"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="h-5 w-5 border-2 border-white border-opacity-50 border-t-white rounded-full animate-spin" />
                        Resetting password...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2 justify-center">
                        Reset Password
                        <Check className="h-5 w-5" />
                      </span>
                    )}
                  </Button>
                </div>
                
                <div className="flex justify-center">
                  <Button 
                    variant="ghost" 
                    onClick={handleClose}
                    className="text-sm"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
} 